import { useState } from "react";
import { createPortal } from "react-dom";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { SALUTATIONS } from "@/lib/salutations";

export const AttendeeEdit = ({ eventId, attendee, onClose, onSaved }) => {
  const [form, setForm] = useState({
    salutation: attendee.salutation || "",
    first_name: attendee.first_name || "",
    last_name: attendee.last_name || "",
    email: attendee.email || "",
    phone: attendee.phone || "",
    company: attendee.company || "",
    job_title: attendee.job_title || "",
    dietary: attendee.dietary || "",
    note: attendee.note || "",
    table: attendee.table || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const custom = form.salutation && !SALUTATIONS.includes(form.salutation);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(
        `/events/${eventId}/registrations/${attendee.registration_id}`,
        form
      );
      toast.success("Attendee details updated");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/80 overflow-y-auto p-4" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="card p-7 max-w-2xl w-full mx-auto my-8"
        data-testid="attendee-edit-modal"
      >
        <h3 className="font-display text-lg font-black">Edit attendee</h3>
        <p className="text-xs text-slate-400 mt-2">
          Their QR pass and reference{" "}
          <span className="font-mono2 text-slate-200">{attendee.registration_code}</span> stay the
          same, so anything already issued keeps working. Correcting an email does not resend
          anything — use the Send buttons on their row if they need a fresh copy.
        </p>

        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          <div>
            <label className="label">Title</label>
            <select
              className="input"
              data-testid="attendee-edit-salutation"
              value={custom ? "__custom" : form.salutation}
              onChange={(e) =>
                setForm({ ...form, salutation: e.target.value === "__custom" ? form.salutation : e.target.value })
              }
            >
              <option value="">—</option>
              {SALUTATIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              {custom && <option value="__custom">{form.salutation}</option>}
            </select>
            <input
              className="input mt-2"
              maxLength={24}
              placeholder="Or type a title"
              data-testid="attendee-edit-salutation-other"
              value={form.salutation}
              onChange={set("salutation")}
            />
          </div>
          <div>
            <label className="label">First name</label>
            <input className="input" required data-testid="attendee-edit-first-name"
              value={form.first_name} onChange={set("first_name")} />
          </div>
          <div>
            <label className="label">Last name</label>
            <input className="input" required data-testid="attendee-edit-last-name"
              value={form.last_name} onChange={set("last_name")} />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Email</label>
            <input type="email" className="input" required data-testid="attendee-edit-email"
              value={form.email} onChange={set("email")} />
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="input" data-testid="attendee-edit-phone"
              value={form.phone} onChange={set("phone")} />
          </div>
          <div>
            <label className="label">Company</label>
            <input className="input" data-testid="attendee-edit-company"
              value={form.company} onChange={set("company")} />
          </div>
          <div>
            <label className="label">Job title</label>
            <input className="input" data-testid="attendee-edit-job-title"
              value={form.job_title} onChange={set("job_title")} />
          </div>
          <div>
            <label className="label">Table / seating</label>
            <input className="input" maxLength={40} placeholder="e.g. Table 7"
              data-testid="attendee-edit-table"
              value={form.table} onChange={set("table")} />
          </div>
          <div className="sm:col-span-3">
            <label className="label">Dietary needs</label>
            <input className="input" maxLength={300} data-testid="attendee-edit-dietary"
              value={form.dietary} onChange={set("dietary")} />
          </div>
          <div className="sm:col-span-3">
            <label className="label">Note (organiser only)</label>
            <input className="input" maxLength={300} placeholder="e.g. replied by phone"
              data-testid="attendee-edit-note"
              value={form.note} onChange={set("note")} />
            <p className="text-[11px] text-slate-500 mt-1.5">
              Only your team sees this. It is never shown to the guest and never emailed.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-7">
          <button className="btn-primary" disabled={busy} data-testid="attendee-edit-save-btn">
            <Save size={15} /> {busy ? "Saving…" : "Save changes"}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary"
            data-testid="attendee-edit-cancel-btn">
            Cancel
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};
